import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, CircleDot, Plus, RefreshCw, Loader2 } from "lucide-react";
import BoardFixtureRow from "./BoardFixtureRow";
import BoardLeagueRail from "./BoardLeagueRail";
import {
  AUTH_FILTERS,
  getP2PFilterHint,
  P2P_FILTERS,
  P2P_SORT_OPTIONS,
  P2P_STATUS_FILTERS,
} from "../../../utils/p2pHelpers";

const P2PIndex = ({
  bets,
  boardSections = [],
  expandedFixtures,
  onToggleFixture,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  isAuthenticated,
  statusCounts,
  sortBy,
  onSortChange,
  leagueOptions,
  leagueFilter,
  onLeagueChange,
  openOnly,
  onOpenOnlyChange,
  onRefresh,
  refreshing,
  isLoading,
  totalCount,
  filteredCount,
  leagueRailTotal,
  favoriteTeam,
  hasMore,
  onLoadMore,
  loadingMore,
}) => {
  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const moreFiltersRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 901px)");
    const sync = () => {
      if (moreFiltersRef.current) {
        moreFiltersRef.current.open = mq.matches;
      }
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const filterHint = getP2PFilterHint(filter, favoriteTeam);
  const createTo = isAuthenticated ? "/app/p2p/create" : "/app/login";
  const createState = isAuthenticated ? undefined : { from: "/app/p2p/create" };
  const countLabel =
    filteredCount === totalCount
      ? `${filteredCount} P2P`
      : `${filteredCount} / ${totalCount}`;

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [leagueFilter]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "auto" });
      }
    }
  }, [selectedId]);

  return (
    <aside className="pit-index" aria-label="P2P bets">
      <header className="pit-index-head">
        <div className="pit-index-intro">
          <div className="pit-index-intro-text">
            <h2 className="pit-index-title">P2P bets</h2>
            <p className="pit-index-sub">Public takes · waiting for a counter</p>
          </div>
          <p className="pit-index-count" aria-live="polite">
            {countLabel}
          </p>
        </div>

        <div className="pit-index-status" role="tablist" aria-label="Status">
          {P2P_STATUS_FILTERS.map((status) => (
            <button
              key={status}
              type="button"
              role="tab"
              aria-selected={filter === status}
              className={`pit-index-pill ${
                filter === status ? "pit-index-pill-active" : ""
              }`}
              onClick={() => onFilterChange(status)}
            >
              <span className="pit-index-pill-label">{status}</span>
              <span className="pit-index-pill-num">
                {statusCounts[status] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div
          className="pit-index-scope"
          role="tablist"
          aria-label="Scope"
        >
          {P2P_FILTERS.map((f) => {
            const needsAuth = AUTH_FILTERS.has(f);
            const disabled = needsAuth && !isAuthenticated;
            return (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                disabled={disabled}
                title={disabled ? "Log in required" : undefined}
                className={`pit-index-scope-chip ${
                  filter === f ? "pit-index-scope-chip-active" : ""
                } ${disabled ? "pit-index-scope-chip-disabled" : ""}`}
                onClick={() => onFilterChange(f)}
              >
                {f}
              </button>
            );
          })}
        </div>

        {filterHint && <p className="pit-index-hint">{filterHint}</p>}

        <div className="pit-index-toolbar">
          <div className="pit-index-controls-row">
            <label className="pit-index-field pit-index-field-grow">
              <span className="pit-index-field-label">Sort</span>
              <span className="pit-index-select-wrap">
                <select
                  value={sortBy}
                  onChange={(e) => onSortChange(e.target.value)}
                  aria-label="Sort P2P bets"
                >
                  {P2P_SORT_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={11} className="pit-index-select-chev" aria-hidden />
              </span>
            </label>
            <button
              type="button"
              className="pit-index-refresh"
              onClick={onRefresh}
              disabled={refreshing || isLoading}
              aria-label="Refresh P2P bets"
            >
              {refreshing ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
            </button>
          </div>

          <details ref={moreFiltersRef} className="pit-index-filters">
            <summary>More filters</summary>
            <div className="pit-index-controls">
              {leagueOptions.length > 1 && (
                <label className="pit-index-field">
                  <span className="pit-index-field-label">League</span>
                  <span className="pit-index-select-wrap">
                    <select
                      value={leagueFilter}
                      onChange={(e) => onLeagueChange(e.target.value)}
                      aria-label="Filter by league"
                    >
                      <option value="">All leagues</option>
                      {leagueOptions.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name} ({l.count})
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={11} className="pit-index-select-chev" aria-hidden />
                  </span>
                </label>
              )}

              <div className="pit-index-controls-foot">
                <label className="pit-index-open">
                  <input
                    type="checkbox"
                    checked={openOnly}
                    onChange={(e) => onOpenOnlyChange(e.target.checked)}
                  />
                  <span>Open capacity only</span>
                </label>
                <p className="pit-index-keys" aria-hidden>
                  <kbd>j</kbd>/<kbd>k</kbd>
                </p>
              </div>
            </div>
          </details>

          <Link to={createTo} state={createState} className="pit-index-create">
            <Plus size={12} strokeWidth={2.5} />
            <span>Create P2P</span>
          </Link>
        </div>
      </header>

      {leagueOptions.length > 0 && (
        <BoardLeagueRail
          leagues={leagueOptions}
          selectedId={leagueFilter}
          onSelect={onLeagueChange}
          totalCount={leagueRailTotal ?? filteredCount}
        />
      )}

      <div className="pit-index-list pmb-board" ref={listRef}>
        {isLoading && bets.length === 0 ? (
          <div className="pit-index-skel">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="pit-index-skel-row" aria-hidden />
            ))}
          </div>
        ) : boardSections.length === 0 ? (
          <div className="pit-index-empty">
            <p className="pit-index-empty-title">No P2P bets here</p>
            <p className="pit-index-empty-body">
              Try another filter or be the first to create one.
            </p>
            {(filter !== "All" || leagueFilter || openOnly) && (
              <button
                type="button"
                className="pit-index-empty-reset"
                onClick={() => {
                  onFilterChange("All");
                  onLeagueChange("");
                  onOpenOnlyChange(false);
                }}
              >
                Reset filters
              </button>
            )}
          </div>
        ) : (
          boardSections.map((section) => (
            <section key={section.leagueId} className="pmb-league">
              <button
                type="button"
                className={`pmb-league-head${
                  leagueFilter === section.leagueId ? " pmb-league-head-on" : ""
                }`}
                onClick={() =>
                  onLeagueChange(
                    leagueFilter === section.leagueId ? "" : section.leagueId
                  )
                }
                aria-pressed={leagueFilter === section.leagueId}
                title={
                  leagueFilter === section.leagueId
                    ? "Show all leagues"
                    : `Show only ${section.leagueName}`
                }
              >
                <span className="pmb-league-ident">
                  {section.leagueLogo ? (
                    <img
                      src={section.leagueLogo}
                      alt=""
                      className="pmb-league-logo"
                    />
                  ) : (
                    <span className="pmb-league-logo pmb-league-logo-fallback">
                      {(section.leagueName || "L").slice(0, 1)}
                    </span>
                  )}
                  <span className="pmb-league-text">
                    <span className="pmb-league-name" title={section.leagueName}>
                      {section.leagueName}
                    </span>
                    <span className="pmb-league-sub">
                      {section.fixtures.length} match
                      {section.fixtures.length === 1 ? "" : "es"}
                      {section.hasLive ? " · live now" : ""}
                    </span>
                  </span>
                  {section.hasLive && (
                    <span className="pmb-league-live" aria-label="Live matches">
                      <CircleDot size={8} aria-hidden />
                    </span>
                  )}
                </span>
                <span className="pmb-league-count">
                  <strong>{section.offerCount}</strong>
                  <small>offers</small>
                </span>
              </button>

              <div className="pmb-league-body">
                {section.fixtures.map((entry) => {
                  const isExpanded = expandedFixtures?.has(entry.fixtureId);
                  const holdsSelected = entry.offers.some(
                    (o) => o._id === selectedId
                  );
                  return (
                    <div
                      key={entry.fixtureId}
                      ref={holdsSelected ? selectedRef : null}
                    >
                      <BoardFixtureRow
                        entry={entry}
                        expanded={isExpanded}
                        onToggle={onToggleFixture}
                        selectedId={selectedId}
                        onSelectOffer={onSelect}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
        {hasMore && boardSections.length > 0 && (
          <button
            type="button"
            className="pit-index-load-more"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Loading…
              </>
            ) : (
              "Load more"
            )}
          </button>
        )}
      </div>
    </aside>
  );
};

export default P2PIndex;
